import {
  DataFlowEdgeForValidation,
  ProcessFlowchartForValidation,
} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {
  ensureDataFlowEdgeSourceIsValid,
  getSourceAndTargetFieldsForDataFlowEdge,
} from 'processes/graph-builder-processes/validation/validateProcessFlowchartRules';
import buildValidationFlowchartFromStaticData from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/buildValidationFlowchartFromStaticData';
import {
  FlowchartStaticEdgeData,
  ProcessFlowchartEdgeType,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {
  FlowchartEdgeChangeType,
  FlowchartNodeChangeType,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorUtils';
import {FlowchartConfigWithMetadata} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/redux/flowchartConfigAutosaveManagerCallbacks';
import {getTransitionSourceByNodeIdPairKey} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/TransitionSourceUtils';
import {
  FlowchartChangeGenerator,
  FlowchartChanges,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useFlowchartState/FlowchartStateTypes';

/**
 * Collects the IDs of data flow edges that are affected by node schema updates.
 * Returns edges that are connected to (incoming or outgoing from) the updated nodes.
 */
export function collectAffectedDataFlowEdgeIdsForNodeUpdates(
  flowchartChanges: FlowchartChanges,
  edges: Array<FlowchartStaticEdgeData>
): Array<string> {
  const affectedEdgeIds = new Set<string>();
  const updatedNodeIds = new Set<string>();

  for (const change of flowchartChanges.nodeChanges) {
    if (change.type === FlowchartNodeChangeType.REPLACE) {
      updatedNodeIds.add(change.id);
    }
  }

  for (const edge of edges) {
    if (edge.type === ProcessFlowchartEdgeType.DATA_FLOW) {
      if (updatedNodeIds.has(edge.source) || updatedNodeIds.has(edge.target)) {
        affectedEdgeIds.add(edge.id);
      }
    }
  }

  return Array.from(affectedEdgeIds);
}

/**
 * Collects the IDs of data flow edges that are affected by transition source changes.
 * Returns edges between the specific node pairs that had transition source changes.
 */
export function collectAffectedDataFlowEdgeIdsForTransitionSourceChanges(
  flowchartChanges: FlowchartChanges,
  edges: Array<FlowchartStaticEdgeData>
): Array<string> {
  const affectedEdgeIds = new Set<string>();
  const transitionSourceNodePairs = new Set<string>();

  for (const change of flowchartChanges.transitionSourceChanges) {
    transitionSourceNodePairs.add(change.id);
  }

  for (const edge of edges) {
    if (edge.type === ProcessFlowchartEdgeType.DATA_FLOW) {
      const edgeNodePairKey = getTransitionSourceByNodeIdPairKey(edge.source, edge.target);
      if (transitionSourceNodePairs.has(edgeNodePairKey)) {
        affectedEdgeIds.add(edge.id);
      }
    }
  }

  return Array.from(affectedEdgeIds);
}

export function doesDataFlowEdgeHaveArchivedOrDeletedFields(
  edge: DataFlowEdgeForValidation,
  flowchart: ProcessFlowchartForValidation
): boolean {
  const sourceAndTargetFields = getSourceAndTargetFieldsForDataFlowEdge(edge, flowchart);
  if (sourceAndTargetFields == null) {
    return true;
  }
  if (
    (sourceAndTargetFields.sourceField.archiveData != null &&
      sourceAndTargetFields.sourceField.archiveData.archived) ||
    (sourceAndTargetFields.targetField.archiveData != null &&
      sourceAndTargetFields.targetField.archiveData.archived)
  ) {
    return true;
  }
  return false;
}

/**
 * Type for an optional function that computes additional edge IDs to remove based on application-specific logic.
 * Takes the flowchart changes and validation flowchart, and returns edge IDs that should be deleted.
 */
export type ApplicationEdgeRemovalFn = (
  changes: FlowchartChanges,
  validationFlowchart: ProcessFlowchartForValidation
) => Array<string>;

export function healDataFlowEdges(
  changes: FlowchartChanges,
  getAdditionalEdgesToRemove?: ApplicationEdgeRemovalFn
): FlowchartChangeGenerator {
  return (currentFlowchart: FlowchartConfigWithMetadata) => {
    // Check for data flow validation errors
    const validationFlowchart = buildValidationFlowchartFromStaticData({
      id: currentFlowchart.metadata.currentVersionId ?? '',
      staticNodesData: currentFlowchart.staticFlowchart.nodesStaticData,
      staticEdgesData: currentFlowchart.staticFlowchart.edgesStaticData,
      options: currentFlowchart.metadata,
    });
    const affectedDataFlowEdgeIds = [
      ...collectAffectedDataFlowEdgeIdsForNodeUpdates(
        changes,
        currentFlowchart.staticFlowchart.edgesStaticData
      ),
      ...collectAffectedDataFlowEdgeIdsForTransitionSourceChanges(
        changes,
        currentFlowchart.staticFlowchart.edgesStaticData
      ),
    ];
    const edgesToRemove: Array<string> = [];
    // Check affected data flow edges for validation errors (invalid source types)
    for (const edge of validationFlowchart.flowchart.edges) {
      if (edge.type === ProcessFlowchartEdgeType.DATA_FLOW && affectedDataFlowEdgeIds.includes(edge.id)) {
        const validationErrors =
          ensureDataFlowEdgeSourceIsValid({
            edge,
            flowchart: validationFlowchart.flowchart,
            usingFlowchartFirstConfig: false,
          }) ?? [];
        if (validationErrors.length > 0) {
          edgesToRemove.push(edge.id);
        }
        // Remove data flow edges with archived source or target fields
        if (doesDataFlowEdgeHaveArchivedOrDeletedFields(edge, validationFlowchart.flowchart)) {
          edgesToRemove.push(edge.id);
        }
      }
    }

    // Add any additional application-specific edges to remove
    if (getAdditionalEdgesToRemove) {
      const additionalEdgeIds = getAdditionalEdgesToRemove(changes, validationFlowchart.flowchart);
      edgesToRemove.push(...additionalEdgeIds);
    }

    // If there are validation errors, remove the data flow edges that are causing the errors
    return {
      edgeChanges: edgesToRemove.map((edgeId) => ({
        type: FlowchartEdgeChangeType.REMOVE,
        id: edgeId,
      })),
    };
  };
}
